'use client';

import { memo, useCallback } from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * Inserts markdown syntax around the current selection in a textarea.
 * If no text is selected, inserts the syntax with placeholder text.
 */
function insertMarkdownSyntax(
  textarea: HTMLTextAreaElement,
  prefix: string,
  suffix: string,
  placeholder: string,
  setValue: (value: string) => void
) {
  const { selectionStart, selectionEnd, value } = textarea;
  const selected = value.substring(selectionStart, selectionEnd);
  const content = selected || placeholder;
  const newValue =
    value.substring(0, selectionStart) +
    prefix +
    content +
    suffix +
    value.substring(selectionEnd);
  setValue(newValue);

  const cursorPos = selectionStart + prefix.length;
  const cursorEnd = cursorPos + content.length;
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(
      selected ? cursorEnd : cursorPos,
      cursorEnd
    );
  });
}

/**
 * Inserts a line-level markdown prefix (e.g., "- " or "1. ") at the beginning of the current line.
 */
function insertLineSyntax(
  textarea: HTMLTextAreaElement,
  linePrefix: string,
  setValue: (value: string) => void
) {
  const { selectionStart, value } = textarea;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  const newValue =
    value.substring(0, lineStart) + linePrefix + value.substring(lineStart);
  setValue(newValue);

  const cursorPos = selectionStart + linePrefix.length;
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(cursorPos, cursorPos);
  });
}

const TOOLBAR_ACTIONS = [
  { icon: 'mdi:format-bold', tooltip: 'Bold', prefix: '**', suffix: '**', placeholder: 'bold text' },
  { icon: 'mdi:format-italic', tooltip: 'Italic', prefix: '_', suffix: '_', placeholder: 'italic text' },
  { icon: 'mdi:code-tags', tooltip: 'Inline code', prefix: '`', suffix: '`', placeholder: 'code' },
  { icon: 'mdi:format-strikethrough-variant', tooltip: 'Strikethrough', prefix: '~~', suffix: '~~', placeholder: 'text' },
] as const;

const LINE_ACTIONS = [
  { icon: 'mdi:format-list-bulleted', tooltip: 'Bulleted list', linePrefix: '- ' },
  { icon: 'mdi:format-list-numbered', tooltip: 'Numbered list', linePrefix: '1. ' },
  { icon: 'mdi:format-quote-close', tooltip: 'Quote', linePrefix: '> ' },
] as const;

export interface MarkdownToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

function MarkdownToolbarImpl({
  textareaRef,
  onValueChange,
  disabled = false,
}: MarkdownToolbarProps) {
  const handleToolbarAction = useCallback(
    (prefix: string, suffix: string, placeholder: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      insertMarkdownSyntax(textarea, prefix, suffix, placeholder, onValueChange);
    },
    [textareaRef, onValueChange]
  );

  const handleLineAction = useCallback(
    (linePrefix: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      insertLineSyntax(textarea, linePrefix, onValueChange);
    },
    [textareaRef, onValueChange]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.5,
        py: 0.25,
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {TOOLBAR_ACTIONS.map((action) => (
        <Tooltip key={action.tooltip} title={action.tooltip}>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() =>
              handleToolbarAction(action.prefix, action.suffix, action.placeholder)
            }
            sx={{ borderRadius: 0.5 }}
          >
            <Icon icon={action.icon} width={18} />
          </IconButton>
        </Tooltip>
      ))}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {LINE_ACTIONS.map((action) => (
        <Tooltip key={action.tooltip} title={action.tooltip}>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => handleLineAction(action.linePrefix)}
            sx={{ borderRadius: 0.5 }}
          >
            <Icon icon={action.icon} width={18} />
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
}

export const MarkdownToolbar = memo(MarkdownToolbarImpl);

export default MarkdownToolbar;
